import React, { forwardRef, SelectHTMLAttributes, useId } from 'react';
import { ChevronDown } from 'lucide-react';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterDropdownProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'size'> {
  label: string;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  includeAllOption?: boolean;
  allOptionLabel?: string;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
  inline?: boolean;
}

const sizeStyles = {
  sm: 'px-3 py-1.5 text-sm pr-8 min-h-[36px]',
  md: 'px-3 py-2 text-sm pr-10 min-h-[44px]',
  lg: 'px-4 py-2.5 text-base pr-10 min-h-[48px]',
};

export const FilterDropdown = forwardRef<HTMLSelectElement, FilterDropdownProps>(
  (
    {
      label,
      options,
      value,
      onChange,
      className = '',
      includeAllOption = true,
      allOptionLabel = 'All',
      fullWidth = false,
      size = 'md',
      inline = false,
      disabled,
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value);
    };

    const containerClasses = inline
      ? 'inline-flex items-center gap-2'
      : `max-w-full ${fullWidth ? 'w-full' : ''}`;

    const labelClasses = inline
      ? 'text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap'
      : 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

    return (
      <div className={containerClasses}>
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
        <div className={`relative ${inline ? '' : fullWidth ? 'w-full' : ''} max-w-full`}>
          <select
            ref={ref}
            id={id}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            className={`
              block rounded-lg border bg-white dark:bg-gray-800
              appearance-none cursor-pointer truncate
              focus:outline-none focus:ring-2 focus:ring-offset-0
              focus:border-blue-500 focus:ring-blue-500
              dark:focus:border-blue-400 dark:focus:ring-blue-400
              disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
              dark:disabled:bg-gray-900 dark:disabled:text-gray-600
              border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100
              ${sizeStyles[size]}
              ${fullWidth ? 'w-full' : 'max-w-full'}
              ${className}
            `.trim().replace(/\s+/g, ' ')}
            {...props}
          >
            {includeAllOption && (
              <option value="">{allOptionLabel}</option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          </div>
        </div>
      </div>
    );
  }
);

FilterDropdown.displayName = 'FilterDropdown';
